import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import type { RouteProps } from 'react-router-dom';

import { useAuth } from '../../shared';
import { PermissionCheck } from '../../shared';
import DynamicPageTitle from '../DynamicPageTitle';

type ProtectedRouteProps = AppRouteSingleType &
  Omit<RouteProps, 'render' | 'component' | 'children'> & {
    title?: React.ReactNode;
    children?: React.ReactNode;
  };

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  title,
  action,
  children,
  groupUrl,
  component: Component,
  ...rest
}) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return null;
  if (!isAuthenticated) return <Redirect to="/login" />;

  const baseElement = (
    <>
      {<DynamicPageTitle>{title}</DynamicPageTitle>}
      {Component ? <Component /> : children}
    </>
  );

  // wrap with groupUrl Route that adds group pattern to the route
  // this way route is at /subroute/id, but matches as /subroute/:id
  // and useRouteMatch matches with actual groupUrl template
  const element = groupUrl ? (
    <Route path={groupUrl}>{baseElement}</Route>
  ) : (
    baseElement
  );

  if (!action || !action.length) {
    return <Route {...rest} children={element} />;
  }

  return (
    <Route {...rest}>
      <PermissionCheck action={action} fallback={<Redirect to="/404" />}>
        {element}
      </PermissionCheck>
    </Route>
  );
};

export default React.memo(ProtectedRoute);
